import { useEffect, useReducer } from "react";
import { getRemainingTokens } from "../api";
import { TokenData } from "../types/types";

interface UseRemainingTokensResult {
    data: TokenData | null;
    loading: boolean;
    isTextExhausted: boolean;
    isAudioExhausted: boolean;
    refetch: () => Promise<void>;
}

let cache: TokenData | null = null;
let cachedToken: string | null = null;
let loading = false;
let inFlight: Promise<void> | null = null;
const listeners = new Set<() => void>();

const notify = () => {
    listeners.forEach((listener) => listener());
};

const resetCache = () => {
    cache = null;
    cachedToken = null;
    loading = false;
    inFlight = null;
};

const fetchTokens = async (): Promise<void> => {
    const token = localStorage.getItem("token");

    if (!token) {
        if (cache !== null || cachedToken !== null) {
            resetCache();
            notify();
        }
        return;
    }

    if (inFlight && cachedToken === token) {
        return inFlight;
    }

    loading = true;
    notify();

    inFlight = (async () => {
        try {
            const response = await getRemainingTokens({ token });
            cache = response ?? null;
            cachedToken = token;
        } catch (err) {
            console.error("Error fetching remaining tokens:", err);
        } finally {
            loading = false;
            inFlight = null;
            notify();
        }
    })();

    return inFlight;
};

export const useRemainingTokens = (): UseRemainingTokensResult => {
    const [, forceRender] = useReducer((tick: number) => tick + 1, 0);

    useEffect(() => {
        listeners.add(forceRender);

        const currentToken = localStorage.getItem("token");
        if (currentToken !== cachedToken) {
            cache = null;
            cachedToken = null;
            fetchTokens();
        } else if (cache === null && !loading) {
            fetchTokens();
        }

        return () => {
            listeners.delete(forceRender);
        };
    }, []);

    const isTextExhausted = cache !== null && (cache.remainigTextTokens ?? 0) <= 0;
    const isAudioExhausted = cache !== null && (cache.remainigAudioTokens ?? 0) <= 0;

    return {
        data: cache,
        loading,
        isTextExhausted,
        isAudioExhausted,
        refetch: fetchTokens,
    };
};
